import { useEffect, useRef, useState } from 'react';

import {
  ToothFullHeadMPRSliceSelection,
  ToothFullHeadMPRSliceView,
  type ToothFullHeadMPRSlice,
} from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import type {
  FullHeadMPRProjection,
  FullHeadMPRSession,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { reportsModel, useCheckReportSignature } from '@/entities/reports';

import { makeFullHeadMprViewSelectionRequest } from '@/features/MPR';

type SeriesThicknessMm = Partial<Record<FullHeadMPRProjection, number>>;

// The server keys a View record by its whole recipe, so the record it created
// (or re-selected) for the request is the one whose view equals the sent one.
const findSelectedViewRecord = (
  slices: ToothFullHeadMPRSlice[],
  view: ToothFullHeadMPRSliceView,
) =>
  slices.find(
    (slice) =>
      slice.Source.case === 'View' &&
      slice.Selection ===
        ToothFullHeadMPRSliceSelection.ToothFullHeadMPRSliceSelectionAdded &&
      ToothFullHeadMPRSliceView.equals(slice.Source.value, view),
  );

type UseFullHeadMprSliceThicknessOverrideParams = {
  isEnabled: boolean;
  projection: FullHeadMPRProjection | null;
  // The series thickness of every pane: the engine replaces its whole map, so
  // a partial update would reset the other panes to native sampling.
  seriesThicknessMm: SeriesThicknessMm;
  session: FullHeadMPRSession | null;
  toothID: string;
};

// "Only this slice": the thickness slider drives the primary projection of the
// modal session alone and the result is saved as a View slice with its own
// thickness; the series options are never touched.
export const useFullHeadMprSliceThicknessOverride = ({
  isEnabled,
  projection,
  seriesThicknessMm,
  session,
  toothID,
}: UseFullHeadMprSliceThicknessOverrideParams) => {
  const dispatch = useAppDispatch();
  const { checkReportSignature } = useCheckReportSignature();
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const isWritePending = useAppSelector(
    reportsModel.selectors.selectIsFullHeadMprWritePending(toothID),
  );
  const [overrideMm, setOverride] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  // The View record the current override session saved last: every later
  // thickness commit replaces it, so trying 2, 5 and 10 mm leaves one slice.
  const savedRecordIDRef = useRef<string | null>(null);
  const seriesThicknessRef = useRef(seriesThicknessMm);
  seriesThicknessRef.current = seriesThicknessMm;
  const canSave = Boolean(
    toothID && report?.YourPermissions?.CanChangeToothAssets,
  );

  useEffect(() => {
    if (isEnabled) {
      return;
    }
    // The slice saved when the switch goes off is the one the user accepted.
    savedRecordIDRef.current = null;
    if (overrideMm === null) {
      return;
    }
    // Switching the override off hands the projection back to the series.
    setOverride(null);
    session?.setSliceThicknessMm(seriesThicknessRef.current);
  }, [isEnabled, overrideMm, session]);

  useEffect(() => {
    savedRecordIDRef.current = null;
  }, [projection, toothID]);

  // Applied here as well as through the session hook so the snapshot read by
  // saveAsView() in the same handler already carries the new thickness.
  const setOverrideMm = (thicknessMm: number) => {
    if (!isEnabled || !projection) {
      return;
    }
    setOverride(thicknessMm);
    session?.setSliceThicknessMm({
      ...seriesThicknessRef.current,
      [projection]: thicknessMm,
    });
  };

  const saveAsView = () => {
    const snapshot = projection ? session?.getViewSnapshot(projection) : null;
    if (
      !isEnabled ||
      !canSave ||
      isWritePending ||
      savingRef.current ||
      !snapshot
    ) {
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    const finish = () => {
      savingRef.current = false;
      setIsSaving(false);
    };
    checkReportSignature({
      toothID,
      onCancel: finish,
      onSignatureChecked: async () => {
        try {
          const request = makeFullHeadMprViewSelectionRequest({
            snapshot,
            toothID,
          });
          const saved = await dispatch(
            reportsModel.thunks.setToothFullHeadMPRSliceSelection(request),
          );
          if (
            !reportsModel.thunks.setToothFullHeadMPRSliceSelection.fulfilled.match(
              saved,
            ) ||
            request.Target.case !== 'View'
          ) {
            return;
          }
          const previousRecordID = savedRecordIDRef.current;
          const record = findSelectedViewRecord(
            saved.payload.ToothFullHeadMPR?.FullHeadMPRSlices ?? [],
            request.Target.value,
          );
          savedRecordIDRef.current = record?.ID ?? null;
          if (!record || !previousRecordID || previousRecordID === record.ID) {
            return;
          }
          await dispatch(
            reportsModel.thunks.setToothFullHeadMPRSliceSelection({
              ToothID: toothID,
              IsAdded: false,
              Target: { case: 'SliceID', value: previousRecordID },
            }),
          );
        } finally {
          finish();
        }
      },
    });
  };

  return {
    isSaving: isSaving || isWritePending,
    overrideMm: isEnabled ? overrideMm : null,
    saveAsView,
    setOverrideMm,
  };
};
